// Recordatorios: lo que vuelve todos los meses o todos los años y no es una
// tarea puntual. Dominios, planes, respaldos.

import { api } from "../api";
import { esc } from "../html";
import { iconoTilde, iconoMas } from "../iconos";
import { ETIQUETA_PERIODICIDAD, claseUrgencia, textoUrgencia } from "../etiquetas";
import { PERIODICIDADES } from "../../lib/tipos";
import type { ProyectoListado, Recordatorio } from "../../lib/tipos";
import { hoyMontevideo, formatoLocal, urgencia } from "../../lib/fecha";

function fila(r: Recordatorio, hoy: string): string {
  const u = urgencia(r.proxima_fecha, hoy);
  return `
    <div class="fila flex items-start gap-3 p-3">
      <button type="button" class="tilde bCumplir" data-id="${r.id}"
              aria-label="Marcar cumplido" title="Marcar cumplido">${iconoTilde}</button>
      <div class="min-w-0 flex-1">
        <div class="break-words">${esc(r.titulo)}</div>
        <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
          ${u ? `<span class="${claseUrgencia(u)}">${textoUrgencia(u, formatoLocal(r.proxima_fecha))}</span>` : ""}
          <span class="chip">${ETIQUETA_PERIODICIDAD[r.periodicidad]}</span>
          ${r.avisar_dias_antes ? `<span class="chip">avisa ${r.avisar_dias_antes} d antes</span>` : ""}
          ${r.proyecto_nombre ? `<span class="text-xs text-apagado">${esc(r.proyecto_nombre)}</span>` : ""}
        </div>
      </div>
      <button type="button" class="bApagar shrink-0 text-xs text-apagado hover:text-alerta"
              data-id="${r.id}">apagar</button>
    </div>`;
}

export async function renderRecordatorios(contenedor: HTMLElement): Promise<void> {
  async function pintar(): Promise<void> {
    const [recordatorios, proyectos] = await Promise.all([
      api.get<Recordatorio[]>("/api/recordatorios"),
      api.get<ProyectoListado[]>("/api/proyectos"),
    ]);
    const hoy = hoyMontevideo();

    contenedor.innerHTML = `
      <h1 class="titulo-pagina">Recordatorios</h1>
      <p class="subtitulo">Lo que vuelve: dominios, planes, respaldos.</p>

      <div class="mt-4 space-y-2">
        ${
          recordatorios.length
            ? recordatorios.map((r) => fila(r, hoy)).join("")
            : `<div class="tarjeta text-center text-apagado">Todavía no hay recordatorios.</div>`
        }
      </div>

      <details class="tarjeta mt-4">
        <summary class="flex items-center gap-2 font-medium text-acento">${iconoMas} Recordatorio nuevo</summary>
        <form id="fNuevo" class="mt-4 space-y-3">
          <label><span class="etiqueta">Qué hay que hacer</span>
            <input id="titulo" class="campo" required maxlength="200"
                   placeholder="Renovar el dominio de…" /></label>
          <div class="grid grid-cols-2 gap-3">
            <label><span class="etiqueta">Próxima vez</span>
              <input id="fecha" type="date" class="campo" value="${hoy}" required /></label>
            <label><span class="etiqueta">Cada cuánto</span>
              <select id="periodicidad" class="campo">
                ${PERIODICIDADES.map(
                  (p) =>
                    `<option value="${p}"${p === "ANUAL" ? " selected" : ""}>${ETIQUETA_PERIODICIDAD[p]}</option>`,
                ).join("")}
              </select></label>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <label><span class="etiqueta">Avisar días antes</span>
              <input id="avisar" type="number" class="campo" value="7" min="0" max="365" /></label>
            <label><span class="etiqueta">Proyecto</span>
              <select id="proyecto" class="campo">
                <option value="">Ninguno</option>
                ${proyectos.map((p) => `<option value="${p.id}">${esc(p.nombre)}</option>`).join("")}
              </select></label>
          </div>
          <p id="errNuevo" class="text-sm text-alerta"></p>
          <button class="btn-primario w-full">Crear</button>
        </form>
      </details>`;

    contenedor.querySelectorAll<HTMLButtonElement>(".bCumplir").forEach((b) => {
      b.addEventListener("click", async () => {
        b.disabled = true;
        await api.post(`/api/recordatorios/${b.dataset.id}/cumplir`);
        await pintar();
      });
    });

    contenedor.querySelectorAll<HTMLButtonElement>(".bApagar").forEach((b) => {
      b.addEventListener("click", async () => {
        if (!confirm("¿Apagar este recordatorio? Deja de aparecer en la agenda.")) return;
        await api.del(`/api/recordatorios/${b.dataset.id}`);
        await pintar();
      });
    });

    const form = contenedor.querySelector("#fNuevo") as HTMLFormElement;
    form.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const err = contenedor.querySelector("#errNuevo") as HTMLElement;
      err.textContent = "";
      const proyecto = (contenedor.querySelector("#proyecto") as HTMLSelectElement).value;
      try {
        await api.post("/api/recordatorios", {
          titulo: (contenedor.querySelector("#titulo") as HTMLInputElement).value.trim(),
          proxima_fecha: (contenedor.querySelector("#fecha") as HTMLInputElement).value,
          periodicidad: (contenedor.querySelector("#periodicidad") as HTMLSelectElement).value,
          avisar_dias_antes: Number((contenedor.querySelector("#avisar") as HTMLInputElement).value),
          proyecto_id: proyecto ? Number(proyecto) : null,
        });
        await pintar();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo crear";
      }
    });
  }

  try {
    await pintar();
  } catch (e) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">No se pudieron cargar los recordatorios: ${esc(
      e instanceof Error ? e.message : "error desconocido",
    )}</div>`;
  }
}
